import { z } from 'zod'

import { getAiDebugLevel } from '@/Atomic/Widgets/AiChatBot/core/engine/debugStorage'

const orderTuple = z.tuple([z.string(), z.enum(['asc', 'desc'])])

/**
 * Sort-order argument shared by list_entities and get_related. The canonical
 * shape is an array of [field, direction] tuples, but the model frequently
 * sends a single flat pair (`["beginAt", "desc"]`) instead of the nested
 * `[["beginAt", "desc"]]`. Wrap a flat pair so both shapes validate — otherwise
 * the first call fails Zod's tuple check and the model burns a turn
 * self-correcting.
 */
export const orderArg = z.preprocess((value) => {
  if (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === 'string' &&
    (value[1] === 'asc' || value[1] === 'desc')
  ) {
    return [value]
  }
  return value
}, z.array(orderTuple))

/**
 * Log an order-downgrade diagnostic when a query 422s on an unsortable field
 * and the tool retries without order. Gated on AI debug being enabled so it
 * stays silent in production — only surfaces when a developer opts in via
 * `?aiDebug=console`. Shared by list_entities and get_related so the message
 * and the gate stay identical.
 */
export function warnOrderDowngraded(tool: string, order: unknown): void {
  if (getAiDebugLevel()) {
    console.warn(`[${tool}] order downgraded — unsortable field:`, order)
  }
}

/**
 * True for PISA's 422 validation rejection, however the host surfaces it.
 * `.take()` and `.count()` both route through handleCommunicationError and throw
 * a wrapped `DataScopeError` whose message is "A validation has failed" (the
 * httpStatus is dropped), so matching on the raw HTTP error alone would miss a
 * query that failed via `.take()`. A few paths surface the raw error instead, so
 * accept both: a host-neutral structural check for an `httpStatus === 422` field
 * (the shape Scrivito's ClientError carries, without importing the class), plus
 * the wrapped-message match. PISA returns 422 for both an unsortable order field
 * AND a structured `?query=` channel the instance does not support —
 * indistinguishable by the error alone, so callers disambiguate by retrying
 * (drop the order first; if it still fails, fall back to flat).
 */
export function isQueryValidationError(err: unknown): boolean {
  return (
    (typeof err === 'object' &&
      err !== null &&
      'httpStatus' in err &&
      err.httpStatus === 422) ||
    (err instanceof Error && /validation has failed/i.test(err.message))
  )
}
